import React, { useState, useRef, useEffect } from "react";

/**
 * AppImage — drop-in replacement for <img> with:
 *  - Native lazy loading (loading="lazy")
 *  - Intersection Observer fallback for older browsers
 *  - Blur-up placeholder while the real image loads
 *  - Error fallback to a placeholder image
 *
 * Usage is identical to the old component:
 *   <Image src="..." alt="..." className="..." />
 */
function Image({
  src,
  alt = "Image",
  className = "",
  // "eager" for above-the-fold images like hero banners; "lazy" for everything else
  loading = "lazy",
  // Optional: show a low-quality placeholder color while loading
  placeholderColor = "#e5e7eb",
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(loading === "eager");
  const imgRef = useRef(null);

  // Intersection Observer: start loading when image enters the viewport
  useEffect(() => {
    if (loading === "eager" || shouldLoad) return;

    // Use native lazy loading if supported — the browser handles it
    if ("loading" in HTMLImageElement.prototype) {
      setShouldLoad(true);
      return;
    }

    // Fallback: manual IntersectionObserver
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // start loading 200px before it enters view
    );

    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [loading, shouldLoad]);

  const handleLoad = () => setLoaded(true);
  const handleError = (e) => {
    setError(true);
    setLoaded(true);
    e.target.src = "/assets/images/no_image.png";
  };

  return (
    <span
      ref={imgRef}
      className="relative block overflow-hidden"
      style={{ background: loaded ? "transparent" : placeholderColor }}
      aria-hidden={!alt}
    >
      {/* Blur placeholder — fades out once the real image loads */}
      {!loaded && (
        <span
          className="absolute inset-0 animate-pulse"
          style={{ background: placeholderColor }}
          aria-hidden="true"
        />
      )}

      {shouldLoad && (
        <img
          src={error ? "/assets/images/no_image.png" : src}
          alt={alt}
          loading={loading}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={[
            className,
            "transition-opacity duration-500",
            loaded ? "opacity-100" : "opacity-0",
          ]
            .filter(Boolean)
            .join(" ")}
          {...props}
        />
      )}
    </span>
  );
}

export default Image;
